import { Component, inject } from '@angular/core';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import {
  ALLOWED_MIME_TYPES,
  HIDE_IMAGE_SENTINEL,
  MAX_FILE_SIZE_MB,
  USE_DEFAULT_SENTINEL,
} from '@modules/report-generator/constants';
import { TemplateImage } from '@modules/report-generator/interfaces';
import { ReportGeneratorStore } from '@modules/report-generator/report-generator.store';

@Component({
  selector: 'app-select-image-dialog',
  templateUrl: './select-image-dialog.component.html',
  styleUrls: ['./select-image-dialog.component.scss'],
})
export class SelectImageDialogComponent {
  private readonly store = inject(ReportGeneratorStore);
  readonly dialogRef = inject(MatDialogRef<SelectImageDialogComponent>);
  readonly data = inject<{
    images: TemplateImage[];
    templateId: number;
    imagesUsedInTemplate: number[];
    canHideImage: boolean;
  }>(MAT_DIALOG_DATA);

  images = [...this.data.images];
  selectedImageId: number | null = null;
  uploadError: string | null = null;

  isImageUsedInTemplate(imageId: number): boolean {
    return this.data.imagesUsedInTemplate.includes(imageId);
  }

  get selectedImageIsDeleteable(): boolean {
    if (!this.selectedImageId) return false;
    return !this.data.imagesUsedInTemplate.includes(this.selectedImageId);
  }

  onSelectImage(image: TemplateImage): void {
    this.selectedImageId = image.id;
  }

  onHideImage(): void {
    this.dialogRef.close(HIDE_IMAGE_SENTINEL);
  }

  onUseDefault(): void {
    this.dialogRef.close(USE_DEFAULT_SENTINEL);
  }

  onUploadNewImage(file: File): void {
    this.store.uploadTemplateImage(this.data.templateId, file).subscribe({
      next: (newImage) => {
        this.images = [...this.images, newImage];
        this.selectedImageId = newImage.id;
      },
      error: (err) => console.error('Image upload failed', err),
    });
  }

  onDeleteImage(): void {
    if (!this.selectedImageId) return;
    this.images = this.images.filter((img) => img.id !== this.selectedImageId);
    this.store.deleteTemplateImage(this.data.templateId, this.selectedImageId);
    this.selectedImageId = null;
  }

  onFileInputChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    const fileSizeMb = file.size / (1024 * 1024);
    if (fileSizeMb > MAX_FILE_SIZE_MB) {
      this.uploadError = `File size must be under ${MAX_FILE_SIZE_MB}MB`;
      return;
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type)) {
      this.uploadError = 'Only JPEG, PNG and WebP images are allowed';
      return;
    }

    this.uploadError = null;
    this.onUploadNewImage(file);
  }

  onConfirm(): void {
    this.dialogRef.close(this.selectedImageId);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onClear(): void {
    this.dialogRef.close(-1);
  }
}
